import React, { ReactNode, useEffect, useState } from "react";
import { Flex, Text } from "rebass/styled-components";
import styled from "styled-components";

import { analytics } from "MVVM/Analytics";

interface ITab {
  component: ReactNode;
  name: string;
  title: string;
  type: string;
  // Optional trailing status marker. Additive, so existing callers are
  // unaffected and tabs without one render exactly as before.
  badge?: ReactNode;
}

interface ITabsProps {
  active?: number;
  tabs: ITab[];
  color?: string;
  onTabChange?: (index: number) => void;
}

const Tabs = ({ active, tabs, color, onTabChange }: ITabsProps) => {
  const [activeTab, setActiveTab] = useState(active || 0);

  const updateTabParam = (newTabValue: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", newTabValue);
    window.history.pushState({}, "", url);
  };

  useEffect(() => {
    setActiveTab(active || 0);
  }, [active]);

  return (
    <Flex role="tablist">
      {tabs.length > 1 &&
        tabs.map((tab, index) => (
          <Tab
            key={tab.title}
            active={activeTab === index}
            role="tab"
            aria-selected={activeTab === index}
            tabIndex={0}
            onClick={() => {
              setActiveTab(index);
              analytics.track("Tab Clicked", { type: tab.type });
              updateTabParam(tab.name);
              onTabChange?.(index);
            }}
            // Tabs were div + onClick, so they could not be reached or fired
            // from a keyboard at all.
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              setActiveTab(index);
              analytics.track("Tab Clicked", { type: tab.type });
              updateTabParam(tab.name);
              onTabChange?.(index);
            }}
            flex="1 1 0"
            color={color}
          >
            <Text variant="text.labelSmall">{tab.title}</Text>
            {tab.badge}
          </Tab>
        ))}
    </Flex>
  );
};

const Tab = styled(Flex)<{ active: boolean; color?: string }>`
  align-items: center;
  gap: 6px;
  border-bottom: ${(props) =>
    props.active
      ? `2px solid ${props.color || props.theme.colors.envy}`
      : `2px solid ${props.theme.colors.navy}`};
  color: ${(props) =>
    props.active
      ? props.color || props.theme.colors.envy
      : props.theme.colors.chalk};
  cursor: pointer;
  font-weight: 500;
  justify-content: center;
  padding: 0 0 12px 0;
`;

export { Tabs };
